// src/contexts/MessageStreamingProvider.tsx
"use client";

import React, { ReactNode, useMemo } from 'react';
import { User } from 'firebase/auth';
import type { Document, SelectedItem } from '@/types';
import type { AccessControl } from '@/lib/firebase';
import { MessageStateProvider } from './MessageStateContext';
import { StreamingProvider } from './StreamingContext';
import type { StreamingServiceConfig } from '@/services/streamingService';

interface MessageStreamingProviderProps {
  children: ReactNode;
  assistantId: string;
  assistantConfig: {
    name: string;
    avatar: string;
    initialInstructions: string;
    initialDocuments: Document[];
    tools: string[];
    toolConfigs: Record<string, Record<string, any>>;
    templateId?: string;
    accessControl?: AccessControl;
    adminEmail?: string;
    senderName?: string;
  };
  currentUser: User | null;
  selectedItems: SelectedItem[];
}

/**
 * Level 2: Message & Streaming Context
 * Combines message state management with streaming logic
 * Handles all message-related operations and API calls
 */
export const MessageStreamingProvider: React.FC<MessageStreamingProviderProps> = ({
  children,
  assistantId,
  assistantConfig,
  currentUser,
  selectedItems
}) => {
  // Memoize streaming service config to prevent object recreation
  const streamingServiceConfig = useMemo((): StreamingServiceConfig => ({
    assistantId,
    templateId: assistantConfig.templateId,
    accessControl: assistantConfig.accessControl,
    name: assistantConfig.name,
    avatar: assistantConfig.avatar,
    initialInstructions: assistantConfig.initialInstructions,
    initialDocuments: assistantConfig.initialDocuments,
    tools: assistantConfig.tools,
    toolConfigs: assistantConfig.toolConfigs,
    adminEmail: assistantConfig.adminEmail,
    senderName: assistantConfig.senderName
  }), [assistantId, assistantConfig]);

  return (
    <MessageStateProvider 
      assistantId={assistantId}
      initialSelectedItems={selectedItems}
    >
      <StreamingProvider
        assistantConfig={streamingServiceConfig}
        currentUser={currentUser}
      >
        {children}
      </StreamingProvider>
    </MessageStateProvider>
  );
};